import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';

@Component({
  selector: 'app-attachment-preview-modal',
  templateUrl: './attachment-preview-modal.component.html',
  styleUrls: ['./attachment-preview-modal.component.scss']
})
export class AttachmentPreviewModalComponent implements OnInit {
  @Input() attachments: any[] = [];
  @Input() selectedIndex = 0;

  @Output() closed = new EventEmitter();

  activeAttachment: any = null;
  zoom = 1;
  rotation = 0;
  fileType: string = '';
  safeUrl: SafeResourceUrl | null = null;

  constructor(
    private sanitizer: DomSanitizer,
    private http: HttpClient
  ) {}

  ngOnInit() {
    this.loadAttachment();
  }

  loadAttachment() {
    this.activeAttachment = this.attachments[this.selectedIndex];
    console.log(this.activeAttachment)
    //alert(this.activeAttachment)
    this.zoom = 1;
    this.rotation = 0;
    this.fileType = this.getFileType(this.activeAttachment);
    
    // Sanitize URL for iframe preview
    if (this.fileType === 'pdf' || this.fileType === 'office') {
      const url = this.activeAttachment?.url || this.activeAttachment?.publicUrl;
      if (this.fileType === 'office') {
        // Use Microsoft Office Online Viewer for office documents
        this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
        );
      } else {
        this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      }
    }
  }

  getFileType(attachment: any): string {
    if (!attachment) return 'unknown';

    const fileName = attachment.name || attachment.file_name || '';
    const mimeType = attachment.type || attachment.mime_type || '';
    const url = attachment.url || attachment.publicUrl || '';

    // Check by MIME type first
    if (mimeType) {
      if (mimeType.startsWith('image/')) return 'image';
      if (mimeType.startsWith('video/')) return 'video';
      if (mimeType.startsWith('audio/')) return 'audio';
      if (mimeType === 'application/pdf') return 'pdf';
      if (mimeType.includes('word') || mimeType.includes('document')) return 'office';
      if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'office';
      if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'office';
    }

    // Fallback to file extension
    const ext = (fileName || url).split('.').pop()?.toLowerCase();
    
    // Images
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext || '')) {
      return 'image';
    }
    
    // Videos
    if (['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'].includes(ext || '')) {
      return 'video';
    }
    
    // Audio
    if (['mp3', 'wav', 'ogg', 'aac', 'm4a'].includes(ext || '')) {
      return 'audio';
    }
    
    // PDF
    if (ext === 'pdf') {
      return 'pdf';
    }
    
    // Office Documents
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext || '')) {
      return 'office';
    }
    
    // Text files
    if (['txt', 'csv', 'json', 'xml', 'html', 'css', 'js', 'ts'].includes(ext || '')) {
      return 'text';
    }

    return 'unknown';
  }

  getFileIcon(type: string): string {
    const icons: any = {
      'pdf': 'fa-file-pdf',
      'office': 'fa-file-word',
      'text': 'fa-file-alt',
      'video': 'fa-file-video',
      'audio': 'fa-file-audio',
      'unknown': 'fa-file'
    };
    return icons[type] || 'fa-file';
  }

  downloadFile() {
    const url = this.activeAttachment?.url || this.activeAttachment?.publicUrl;
    const fileName = this.activeAttachment?.name || this.activeAttachment?.file_name || 'download';
    
    // Use Angular HttpClient with blob responseType for proper download
    this.http.get(url, { 
      responseType: 'blob',
      observe: 'response'
    }).subscribe({
      next: (response) => {
        // Create blob from response
        const blob = response.body;
        if (blob) {
          // Create blob URL and trigger download
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          link.style.display = 'none';
          
          document.body.appendChild(link);
          link.click();
          
          // Cleanup
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
          }, 100);
        }
      },
      error: (error) => {
        console.error('Download failed:', error);
        // Fallback: open in new tab if download fails
        window.open(url, '_blank');
      }
    });
  }

  close() {
    this.closed.emit();
  }

  next() {
    this.selectedIndex = (this.selectedIndex + 1) % this.attachments.length;
    this.loadAttachment();
  }

  prev() {
    this.selectedIndex =
      (this.selectedIndex - 1 + this.attachments.length) %
      this.attachments.length;
    this.loadAttachment();
  }

  zoomIn() { 
    if (this.fileType === 'image') {
      this.zoom += 0.1; 
    }
  }
  
  zoomOut() { 
    if (this.fileType === 'image' && this.zoom > 0.2) {
      this.zoom -= 0.1; 
    }
  }
  
  rotate() { 
    if (this.fileType === 'image') {
      this.rotation += 90; 
    }
  }

  // Keyboard shortcuts
  @HostListener('document:keydown', ['$event'])
  onKeydown(e: KeyboardEvent) {
    if (e.key === 'ArrowRight') this.next();
    if (e.key === 'ArrowLeft') this.prev();
    if (e.key === 'Escape') this.close();
    if (e.key === '+' || e.key === '=') this.zoomIn();
    if (e.key === '-') this.zoomOut();
    if (e.key === 'r' || e.key === 'R') this.rotate();
  }

}
